<template>
  <div>
    <div
      v-if="order.isLoading || orderRoom.joining"
      class="cf-flex cf-flex--column cf-flex--align-center cf-flex--justify-center cf-section-loader-container"
    >
      <div class="cf-loader" />
    </div>

    <div
      v-else
      class="cf-flex cf-flex--column cf-flex--gap-16 cf-phone-padding-16"
    >
      <h1 class="ada-text">
        Your Receipt
      </h1>

      <template v-if="order.orderState !== 'Closed'">
        <user-list />

        <order-room-errors />

        <div
          v-if="orderRoom.orderIsSplit"
          class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
        >
          <span
            class="cf-icon cf-icon--secondary cf-icon--medium cf-icon--block far fa-clock"
            aria-hidden="true"
          />

          <p class="cf-subtitle cf-subtitle--2 cf-text-align-center">
            The bill will close once all guests have authorized their payments.
          </p>

          <template v-if="groupRemainingTotal > 0">
            <p class="cf-title cf-title--6 cf-title--uppercase cf-title--primary">
              Pending Payments:
            </p>

            <div class="cf-flex cf-flex--row cf-flex--justify-space-between cf-flex--gap-16">
              <div class="cf-flex cf-flex--column">
                <ul class="user-name-list">
                  <template v-for="user in orderRoom.otherUsers">
                    <li
                      v-if="!user.paid"
                      :key="user.id"
                      class="user-name-list__item"
                    >
                      {{ user.nickname }}
                    </li>
                  </template>
                </ul>
              </div>

              <div class="cf-flex cf-flex--column">
                <p class="cf-title cf-title--6">
                  {{ $dollars(groupRemainingTotal) }}
                </p>
              </div>
            </div>
          </template>

          <p class="total">
            <span class="total__title">
              <span class="total__title-text">
                Group Total
              </span>
            </span>

            <span class="total__amount">
              {{ $dollars(order.total) }}
            </span>
          </p>
        </div>
      </template>

      <template v-else-if="order.orderState === 'Closed'">
        <div
          v-if="order.orderState === 'Closed'"
          class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
        >
          <span
            class="cf-icon cf-icon--primary cf-icon--medium cf-icon--block far fa-check-circle"
            aria-hidden="true"
          />

          <p
            v-if="orderRoom.orderIsSplit"
            class="total"
          >
            <span class="total__title">
              <span class="total__title-text">
                Group Paid
              </span>
            </span>

            <span class="total__amount">
              {{ $dollars(order.total) }}
            </span>
          </p>

          <p
            v-else
            class="total"
          >
            <span class="total__title">
              <span class="total__title-text">
                You Paid
              </span>
            </span>

            <span class="total__amount">
              {{ $dollars(yourAmount) }}
            </span>
          </p>

          <div class="container max-width-mobile">
            <!--
              this <p> element is on a single line to prevent vue from
              adding a single space character in front of the text, which
              has implications when using `white-space: pre-line`
            -->
            <p class="cf-title cf-title--6 cf-text-pre-line cf-text-align-left">{{ receiptInstructions }}</p>
          </div>
        </div>

        <div
          v-if="session.route.successUrl"
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <div class="container max-width-mobile">
            <p class="cf-subtitle cf-subtitle--2 cf-text-align-center">
              <template v-if="merchant.options.flybuyEnabled">
                Use Flybuy to let us know when you're on your way!
              </template>
              <template v-else>
                Let us know when you're on your way!
              </template>
            </p>

            <a
              class="cf-button cf-button--primary cf-button--lg cf-button--fill"
              :href="session.route.successUrl"
            >Pick Up My Order</a>
          </div>
        </div>

        <div
          v-else-if="order.store.onSiteTextToPayNotificationEnabled
            && (
              $route.query.hasOwnProperty('here')
              || session.route.channelTypeId === 7
              || session.route.channelTypeId === 8
            )
          "
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <div class="container max-width-mobile">
            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
              <p class="cf-subtitle cf-subtitle--2">
                Let us know when you're here to pick up your order.
              </p>

              <guest-arrived-button
                class="no-shrink"
                :auto-open="$route.query.hasOwnProperty('here')"
                :order="order"
                :receipt-token="$route.params.receiptToken"
              />
            </div>
          </div>
        </div>

        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <div class="container max-width-mobile cf-flex cf-flex--column cf-flex--align-center">
            <template v-if="order.showRedemptionQrCode">
              <h2 class="cf-title cf-title--4 cf-title--heavy">
                {{ content.ReceiptQrCodeInstructions.title }}
              </h2>
              <formatted-content :content="content.ReceiptQrCodeInstructions.metadata.subtitle" />
              <vue-qrcode
                class="image image--auto-max image--border extra-spacing"
                :value="order.redemptionQrCodeValue"
                :scale="6"
              />

              <div
                v-if="order.googleWalletJwt || order.applePassUrl"
                class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-8"
              >
                <a
                  v-if="order.applePassUrl"
                  class="cursor-pointer cf-flex"
                  target="_system"
                  @click="handleAddToAppleWallet"
                >
                  <img
                    style="height: var(--input-height)"
                    src="/images/apple-wallet.svg"
                  >
                </a>
                <a
                  v-if="order.googleWalletJwt"
                  target="_blank"
                  class="cursor-pointer cf-flex"
                  :href="`https://pay.google.com/gp/v/save/${order.googleWalletJwt}`"
                >
                  <img
                    style="height: var(--input-height)"
                    src="/images/google-pay-save.svg"
                  >
                </a>

                <formatted-content
                  class="no-spacing"
                  :content="content.PayAtTableQrCodeAndWallet.metadata.saveToWalletSubtitle"
                />
              </div>

              <hr class="extra-spacing-top">
            </template>

            <h2 class="cf-title cf-title--4 cf-title--heavy cf-title--primary">Would you like a receipt?</h2>

            <cf-validated-form
              name="sendReceipt"
              @valid-submit="sendReceipt"
            >
              <div class="cf-flex cf-flex--column cf-flex--gap-16">
                <cf-text-input
                  v-model="email"
                  type="email"
                  label="Email"
                  name="email"
                />

                <cf-phone-number-input
                  v-model="phone"
                  label="Text"
                  name="phone"
                />

                <input-error
                  v-if="!email && !phone"
                  error="Please fill in either an email address or a phone number."
                />

                <button
                  type="submit"
                  class="cf-button cf-button--primary cf-button--lg cf-button--fill"
                  :class="{
                    'cf-button--loading': $store.state.receipt.receiptIsSending
                  }"
                >
                  Send Receipt
                </button>
              </div>
            </cf-validated-form>

            <template v-if="merchant.options.showAppDownloadButtonsOnReceipt && !Boolean(merchant.options.showAppDownloadButtonsOnFooter)">
              <hr class="extra-spacing">
              <app-download-badges
                class="spacing"
                :title="content.ReceiptStandard.metadata.appDownloadText"
              />
            </template>
          </div>
        </div>

        <loyalty-partial-sign-in :receipt="order" />

        <loyalty-receipt-summary
          class="cf-flex cf-flex--column cf-flex--gap-16"
          :is-pay-at-table="true"
          :receipt="order"
        />
      </template>

      <div class="cf-flex cf-flex--column cf-flex--gap-16">
        <div class="container max-width-mobile">
          <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
            <h2 class="cf-title cf-title--6 cf-title--uppercase cf-title--primary">
              {{ content.PayAtTable.metadata.checkNumber }} #{{ order.ticketNumber }}
            </h2>

            <p
              v-if="tableNumberDisplay"
              class="cf-title cf-title--6 cf-title--uppercase"
            >
              {{ userLocationType.description }}
              {{ tableNumberDisplay }}
            </p>
          </div>

          <template v-if="order.orderLevelInstructions">
            <hr class="no-spacing-top">
            <h3 class="cf-title cf-title--7 cf-title--primary">{{ content.PayAtTable.metadata.orderNoteTitle }}</h3>
            <p class="cf-subtitle cf-subtitle--2 cf-text-pre-line">{{ order.orderLevelInstructions }}</p>
          </template>

          <your-payment
            :is-pending="order.orderState !== 'Closed'"
            :order="order"
            :payment="yourPayment"
          />

          <order-items-room
            :disable-item-selection="true"
            :your-totals="{
              payments: yourPayment.payments,
              serviceCharges: yourPayment.serviceCharges,
              orderFees: yourOrderFees,
              orderFeeTotal: yourPayment.storeFees,
              tax: yourPayment.tax,
              tip: yourPayment.tip,
              total: yourPayment.total
            }"
          />

          <button
            v-if="order.orderState === 'Open' && recentlyPaid && !hasExternalDevicePayment"
            type="button"
            class="cf-button cf-button--light cf-button--danger"
            :class="{
              'cf-button--loading': cancellingPayments
            }"
            @click="$openModal('cancelPayments')"
          >
            Cancel Payments
          </button>
        </div>
      </div>

      <div
        v-if="order.attachmentUrl"
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <div class="container max-width-mobile">
          <h3 class="cf-title cf-title--6">Attachment</h3>

          <a
            class="card-button card-button--extra-padding no-spacing-children"
            target="_blank"
            :href="order.attachmentUrl"
            @click="receiptAttachmentClick"
          >
            <div class="card-button__section grow">
              <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                <span class="cf-icon fa-file primary" />
                <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-subtitle--primary">View Attachment</p>
              </div>
            </div>
            <div class="card-button__section card-button__section--icon">
              <span class="cf-icon far fa-external-link gray" />
            </div>
          </a>
        </div>
      </div>

      <survey-link
        v-if="order.orderState === 'Closed'"
        :order="order"
      />

      <modal
        name="cancelPayments"
        modal-style="compact"
      >
        <div class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16">
          <span
            class="cf-icon cf-icon--large cf-icon--block far fa-exclamation-circle cf-icon--danger"
            aria-hidden="true"
          />

          <p class="cf-title cf-title--2">
            Are you sure you want to cancel everyone's payment authorizations?
          </p>

          <button
            type="button"
            class="cf-button cf-button--danger cf-button--lg"
            @click="cancelPayments"
          >
            Cancel Payments
          </button>
          <button
            type="button"
            class="cf-link cf-link--brand-secondary cf-link--bold"
            @click="$closeModal('cancelPayments')"
          >
            Don't Cancel
          </button>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
  import currentTimestampMixin from '@/mixins/current-timestamp';
  import events from '@/services/events';
  import { downloadOrderRedemptionAppleWalletPass } from '@/services/api/order-redemption';
  import logger from '@/services/logger';
  import moment from 'moment-timezone';
  import uaParser from 'ua-parser-js';
  import { mapGetters } from 'vuex';

  import parsePhoneFromApi from '@/helpers/parse-phone-from-api';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';

  export default {
    name: 'PayAtTableReceiptPage',

    components: {
      CfValidatedForm,
      CfPhoneNumberInput,
      CfTextInput
    },

    metaInfo() {
      return {
        title: 'Receipt'
      };
    },

    mixins: [currentTimestampMixin],

    beforeRouteEnter(to, from, next) {
      next((vm) => {
        const isCheckoutFlow = from.name === 'payAtTableCheckout';

        if (isCheckoutFlow) {
          vm.$store.dispatch('merchant/openInAppMessage', { type: 'Receipt' });
        }
      });
    },

    beforeRouteLeave(to, from, next) {
      this.leaveOrderRoom();

      next();
    },

    data() {
      return {
        cancellingPayments: false,
        email: null,
        phone: null
      };
    },

    computed: {
      hasExternalDevicePayment() {
        return this.order.payments.some(payment => payment.paymentType === 'ExternalDevice');
      },

      groupRemainingTotal() {
        const orderTotal = this.order.total - this.order.externalPaymentAmount;
        const totalPaid = this.orderRoom.otherUsers.reduce((paidAmount, user) => {
          if (user.paid) paidAmount += user.total;
          return paidAmount;
        }, this.orderRoom.you.total);

        return orderTotal - totalPaid;
      },

      yourAmount() {
        return this.yourPayment.amount + this.yourPayment.surcharge;
      },

      receiptInstructions() {
        const content = this.merchant.receiptInstructions
          .find(c => c.channelTypeId === this.session.route.channelTypeId);

        const unspecifiedContent = this.merchant.receiptInstructions
          .find(c => c.channelTypeId === 0);

        return content?.instructions
          || unspecifiedContent?.instructions
          || 'Thank you! Please show your waitstaff this screen and you\'re good to go.';
      },

      recentlyPaid() {
        const { currentTimestamp } = this;
        const paymentTimestamp = this.yourPayment.transactionDateTime;

        return !!paymentTimestamp && moment(currentTimestamp).diff(paymentTimestamp, 'minutes') < 15;
      },

      yourOrderFees() {
        const yourStoreFees = this.yourPayment.storeFees;

        return yourStoreFees ? [{ name: 'Your Store Fees', description: '', amount: yourStoreFees }] : [];
      },

      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'tableNumberDisplay', 'userLocationType']),
      ...mapGetters('receipt', ['yourPayment']),
      ...mapGetters('session', ['session']),
      ...mapGetters('socket', ['orderRoom'])
    },

    created() {
      this.loadReceipt();
    },

    methods: {
      async cancelPayments() {
        this.cancellingPayments = true;

        this.$closeModal('cancelPayments');

        try {
          await this.$store.dispatch('orders/cancelAllAuthorizedPayments', { reason: 'manual' });
        }

        catch (error) {
          logger.error(error);

          events.emit('notification', {
            type: 'error',
            message: 'Failed to cancel payments.'
          });
        }

        finally {
          this.cancellingPayments = false;
        }
      },

      async handleAddToAppleWallet() {
        try {
          const paramObject = this.order.applePassUrl
            .split('?')[1]
            .split('&')
            .reduce((obj, param) => {
              const [key, value] = param.split('=');
              obj[key] = value;
              return obj;
            }, {});

          await downloadOrderRedemptionAppleWalletPass(paramObject);
        }

        catch (error) {
          logger.error(error);
          events.emit('notification', {
            clickToDismiss: false,
            duration: 3500,
            message: 'There was an issue generating your pass',
            type: 'error'
          });
        }
      },

      leaveOrderRoom() {
        this.$store.dispatch('socket/leaveOrderRoom');
      },

      loadContactInfo() {
        this.email = this.order.email || this.$store.state.session.emailForReceipt;
        this.phone = parsePhoneFromApi(this.order.phoneNumber || this.$store.state.session.smsNumberForReceipt);
      },

      async loadReceipt() {
        try {
          const options = { receiptToken: this.$route.params.receiptToken };

          const { os, browser } = uaParser(window.navigator.userAgent);
          const isMobileSafari = os.name === 'iOS' && /safari/i.test(browser.name);

          if (this.merchant.options.showOrderQrCode) {
            options.walletType = isMobileSafari ? 'apple' : 'google';
          }

          await this.$store.dispatch('receipt/getPatReceipt', options);

          this.loadContactInfo();

          this.$store.dispatch('socket/joinOrderRoomConditionally');
        }

        catch (error) {
          logger.error(error);

          if (error.response && error.response.status === 404) {
            this.$store.dispatch('socket/paymentsCancelled');
          }

          else {
            events.emit('full-page-message', {
              type: 'generic-p@t-error'
            });
          }
        }
      },

      sendReceipt() {
        const isValid = !!this.email || !!this.phone;

        if (isValid) {
          this.$store.dispatch('receipt/sendReceipt', {
            channelType: this.session.route.channelTypeId,
            email: this.email,
            phone: this.phone,
            receiptToken: this.$route.params.receiptToken
          });
        }
      },

      receiptAttachmentClick() {
        // this.$triggerGoogleTrackingEvent('ViewAttachment', 'Receipt');

        this.$triggerNewRelicEvent('ViewReceiptAttachment', 'Information', 'Viewed Receipt Attachment');
      }
    }
  };
</script>
